import { useEffect, useMemo, useState } from "react";

interface ClientFormState {
  company_name: string;
  email: string;
  full_name: string;
  industry: string;
  plan: "starter" | "growth" | "enterprise";
  mrr: number;
  status: "active" | "paused" | "inactive";
}

type Mode = "create" | "edit";

export default function ClientModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: Mode;
  initial?: Partial<ClientFormState> & { id?: string } | null;
  onClose: () => void;
  onSubmit: (payload: Partial<ClientFormState> & { password?: string }) => Promise<void> | void;
}) {
  const [company_name, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [full_name, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [industry, setIndustry] = useState("");
  const [plan, setPlan] = useState<ClientFormState["plan"]>("starter");
  const [mrr, setMrr] = useState<number>(0);
  const [status, setStatus] = useState<ClientFormState["status"]>("active");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = useMemo(() => {
    if (mode === "create") return "Add Client";
    return "Edit Client";
  }, [mode]);

  useEffect(() => {
    if (!open) return;

    setError(null);
    setSubmitting(false);

    if (mode === "edit" && initial) {
      setCompanyName(initial.company_name ?? "");
      setEmail(initial.email ?? "");
      setFullName(initial.full_name ?? "");
      setIndustry(initial.industry ?? "");
      setPlan((initial.plan as any) || "starter");
      setMrr(typeof initial.mrr === "number" ? initial.mrr : Number(initial.mrr ?? 0));
      setStatus((initial.status as any) || "active");
      setPassword("");
    } else {
      setCompanyName("");
      setEmail("");
      setFullName("");
      setIndustry("");
      setPlan("starter");
      setMrr(0);
      setStatus("active");
      setPassword("");
    }
  }, [open, mode, initial]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (password.trim() && password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }

      setSubmitting(true);
      const payload: any = {
        company_name,
        email,
        full_name,
        industry: industry || undefined,
        plan,
        mrr: Number(mrr) || 0,
        status,
      };

      // Include password if provided (for both create and edit)
      if (password.trim()) {
        payload.password = password;
      }

      await onSubmit(payload);
      setSubmitting(false);
      onClose();
    } catch (err: any) {
      setSubmitting(false);
      setError(err?.message || "Failed to save client");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-dark-panel border border-dark-border rounded-2xl p-6 max-w-xl w-full animate-slide-up">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {title}
            </h3>
            <p className="text-silver text-sm mt-1">{mode === "create" ? "Create a new client account." : "Update client details."}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Company</span>
              <input
                value={company_name}
                onChange={(e) => setCompanyName(e.target.value)}
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                placeholder="Company name"
                required
              />
            </label>
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Plan</span>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value as any)}
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
              >
                <option value="starter">starter</option>
                <option value="growth">growth</option>
                <option value="enterprise">enterprise</option>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Full name</span>
              <input
                value={full_name}
                onChange={(e) => setFullName(e.target.value)}
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                placeholder="Contact person name"
                required
              />
            </label>
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Industry</span>
              <input
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                placeholder="Optional"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Email</span>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                placeholder="user@company.com"
                required
                type="email"
                disabled={mode === "edit"}
              />
              {mode === "edit" && <p className="text-silver-dim text-xs mt-1">Email is immutable.</p>}
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
              >
                <option value="active">active</option>
                <option value="paused">paused</option>
                <option value="inactive">inactive</option>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">MRR (USD)</span>
              <input
                value={String(mrr)}
                onChange={(e) => setMrr(Number(e.target.value))}
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                placeholder="0"
                type="number"
                min={0}
                required
              />
            </label>

            <label className="block">
              <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Password</span>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
                placeholder={mode === "create" ? "Minimum 6 characters" : "Leave blank to keep current password"}
                type="password"
                required={mode === "create"}
              />
              {mode === "edit" && (
                <p className="text-silver-dim text-xs mt-1">If left blank, the client password won&apos;t be changed.</p>
              )}
            </label>
          </div>

          {error && <div className="text-cyber-orange text-sm">{error}</div>}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 transition-all text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                submitting ? "bg-electric-blue/40 text-deep-black cursor-not-allowed" : "bg-electric-blue text-deep-black hover:shadow-glow-blue"
              }`}
            >
              {submitting ? "Saving..." : mode === "create" ? "Create" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

